import Vue from 'vue';
import App from './App.vue';
import VueCookies from 'vue-cookies';
import VueAxios from 'vue-axios';
import axios from 'axios';
import * as CryptoJS from 'crypto-js';

import router from '@/router/index';
import store from '@/store';
import RuntimeConfigurationPlugin from "@/plugins/runtime-configuration-plugin";
import RuntimeConfigurationFaviconsPlugin from "@/plugins/runtime-configuration-favicons-plugin";

export interface RuntimeConfigurationModel {
  NODE_ENV: string;
  VUE_APP_ENVIRONMENT: string;
  VUE_APP_IS_PROD: boolean;
  VUE_APP_FRONT_URL: string;
  VUE_APP_ACCOUNT_URL: string;
  VUE_APP_MEDIAS_URL: string;
  VUE_APP_ACCOUNT_API_URL: string;
  VUE_APP_ACCOUNT_API_KEY: string;
  VUE_APP_API_KEY: string;
  VUE_APP_GOOGLE_RECAPTCHA_SITE_KEY: string;
  VUE_APP_PARTNER_NAME: string;
  VUE_APP_REGISTER_STUDIO_NAME: string;
  VUE_APP_REGISTER_WITH_ACCESS_KEYS: boolean;
  VUE_APP_ENABLE_RECAPTCHA: boolean;
  VUE_APP_ENABLE_CONNECTIONS_SECTION: boolean;
  VUE_APP_ENABLE_NEWSLETTER_SECTION: boolean;
  VUE_APP_ENABLE_DELETION_SECTION: boolean;
  VUE_APP_BYPASS_PARTNER_REGISTER_PAGE: boolean;
  VUE_APP_HAS_CUSTOM_COOKIES_MANAGEMENT: boolean;
  VUE_APP_GOOGLE_TAG_MANAGER: string;
  VUE_APP_PLATFORM_NAME: string;
  VUE_APP_SUPPORT_TEAM_EMAIL: string;
  VUE_APP_ACCOUNT_TERMS_LINK: string;
  VUE_APP_PRIVACY_POLICY_LINK: string;
  VUE_APP_COOKIES_POLICY_LINK: string;
};

axios.get('/runtime-configuration/current/environment.json')
  .then((response) => {
    if (JSON.parse(process.env.VUE_APP_HAS_ENCRYPTED_ENVIRONMENT) == true) {
      return JSON.parse(JSON.parse(DecryptJson(response.data.Value))) as RuntimeConfigurationModel;
    } else {
      return response.data as RuntimeConfigurationModel;
    }
  })
  .then((runtimeConfiguration: RuntimeConfigurationModel) => {
    Vue.config.productionTip = true;
    Vue.use(VueCookies);
    Vue.use(VueAxios, axios);

    Vue.use(RuntimeConfigurationPlugin, {
      configuration: runtimeConfiguration,
    });

    Vue.use(RuntimeConfigurationFaviconsPlugin, {
      mediasUrl: runtimeConfiguration.VUE_APP_MEDIAS_URL,
    });

    Vue.filter('error', function (value: string, parameters?: string[]) {
      if (!value) return '';

      const errors = new Map([
        ["ERROR_NOT_ALLOWED", "You are not allowed to do this action."],
        ["ERROR_EMAIL_UNKNOWN", "This email address is unknown."],
        ["ERROR_PASSWORD_RECOVERY_NOTEXISTS", "Your link is invalid."],
        ["ERROR_PASSWORD_RECOVERY_EXPIRED", "Your link has expired."],
        ["ERROR_EMAIL_RECOVERY_NOTEXISTS", "Your link is invalid."],
        ["ERROR_EMAIL_RECOVERY_EXPIRED", "Your link has expired."],
        ["ERROR_USER_STEAM_EXIST", "This steam account is already linked to another user."],
        ["ERROR_USER_REFERRER_NOTEXISTS", "Your referrer doesn't exist."],
        ["ERROR_USER_USERNAME_LENGTH", "Your username must be between 2 and 25 chars."],
        ["ERROR_USER_USERNAME_PATTERN", "Your username can only contains letters and numbers."],
        ["ERROR_USER_USERNAME_EXIST", "Your username is already taken."],
        ["ERROR_USER_USERNAME_LETTERS", "Your username must contain at least one letter."],
        ["ERROR_USER_USERNAME_EMAIL", "You can not use an email as a username."],
        ["ERROR_USER_PASSWORD_USERNAME", "Your password must not contains your username."],
        ["ERROR_USER_PASSWORD_LENGTH", "Your password must be between 8 and 50 chars."],
        ["ERROR_USER_PASSWORD_LETTERS", "Your password must contains at least 1 letter."],
        ["ERROR_USER_PASSWORD_NUMBERS", "Your password must contains at least 1 number."],
        ["ERROR_USER_CONFIRM_PASSWORD_INVALID", "Your confirmation doesn't match the password."],
        ["ERROR_LASTNAME_EMPTY", "Your lastname should not be empty."],
        ["ERROR_FIRSTNAME_EMPTY", "Your firstname should not be empty."],
        ["ERROR_USER_EMAIL_EMPTY", "Your email is empty."],
        ["ERROR_USER_EMAIL_INVALID", "Your email is invalid."],
        ["ERROR_USER_EMAIL_LENGTH", "Your username must be between 2 and 255 chars."],
        ["ERROR_USER_EMAIL_EXIST", "An account with this email already exists."],
        ["ERROR_USER_EMAIL_INCORRECT_FORMAT", "The format of your email is not valid."],
        ["ERROR_USER_EMAIL_DOMAIN_UNAUTHORIZED", "The domain of your email is not authorized."],
        ["ERROR_USER_EMAIL_DISPOSABLE_ADDRESS", "You can not use a disposable address to create an account."],
        ["ERROR_USER_EMAIL_MAILBOX_DOES_NOT_EXIST", "The mailbox is undeliverable or does not exist."],
        ["ERROR_USER_EMAIL_HIGH_RISK", "Your domain has been identified as high risk and can not be used."],
        ["ERROR_USER_CONFIRM_EMAIL_INVALID", "Your confirmation doesn't match the email."],
        ["ERROR_USER_USERNAME_EMPTY", "Please enter your Username or Email."],
        ["ERROR_USER_PASSWORD_EMPTY", "Please enter your password."],
        ["ERROR_USER_BANNED", "Your account has been banned."],
        ["ERROR_USER_UNKNOWN", "Your username or email is unknown."],
        ["ERROR_USERNAME_PASSWORD_INCORRECT", "Your username or password is incorrect."],
        ["ERROR_APPROVE_PRIVACY_POLICY", "You must acknowledge the Privacy Policy."],
        ["ERROR_APPROVE_EULA", `You must accept the ${parameters && parameters.length > 0 ? parameters[0] : "G2G Account Terms & Conditions"}.`],
        ["ERROR_USER_CONFIRMED", "Your must confirm your address email."],
        ["ERROR_USER_NOT_EXISTS", "Your username, email or password is incorrect."],
        ["ERROR_USER_IP_BANNED", "You are not allowed to do this action because your IP is banned."],
        ["ERROR_USER_IP_BANNED_LOGIN", "You are not allowed to log in because your IP is banned."],
        ["ERROR_USER_IP_BANNED_REGISTER", "You are not allowed to create an account because your IP is banned."],
        ["ERROR_USER_IP_BANNED_STEAM_LINK", "You are not allowed to link your steam account because your IP is banned."],
        ["PARTNER_RESPONSE_ERROR", "An error occured validating partner authentication."],
        ["ERROR_STEAM_NOT_REACHABLE", "Steam API is not reachable right now. Please try again later."],
        ["ERROR_ACCESS_KEY_NON_EXISTANT", "The key is invalid"],
        ["ERROR_ACCESS_KEY_ALREADY_USED", "The key is already used"],
        ["ERROR_OPTIN", "An error occurred, please try again later."],
        ["ERROR_OPTIN_NOT_EXISTS", "The list id does not exist."],
        ["ERROR_REGISTER_NO_SUCCESS", "An error occured. Please try again later."],
        ["ERROR_REGISTER_INVALID", "You're not allowed to create an account."],
        ["ERROR_REGISTER_TOKEN_EMPTY", "You need a valid token to create an account."],
        ["ERROR_SEGA_RESET_PASSWORD", "Could not reset password"],
        ["ERROR_INVALID_REGISTER", "Registration failed"],
        ["TOKEN_INVALID", "Invalid token"],
        ["TOKEN_INVALID_REFRESH", "Invalid token to refresh"],
        ["TOKEN_USER_INVALID", "Can't create token for unauthenticated user"],
        ["ERROR_EMPTY_MANDATORY_FIELD", "Please complete this field"],
        ["ERROR_INCOMPLETE_MANDATORY_FIELD", "This field isn't complete"],
        ["ERROR_DATEOFBIRTH_EMPTY", "Please complete this field"],
        ["ERROR_DATEOFBIRTH_BELOW_THRESHOLD_AGE", `You must be at least ${parameters ? parameters[0] : ""} to register`],
        ["ERROR_IMPOSSIBLE_DATEOFBIRTH", 'Please enter a valid birthdate'],
      ]);

      const error = errors.get(value);
      return error != null ? error : value;
    });

    router.beforeEach(async (to, from, next) => {
      if (to && to.meta != null) {
        document.title = 'Account - ' + (to.meta.title || 'Games2Gether');
      }

      // waiting for environment variables to be set in $data context
      await Vue.nextTick();
      next();
    });

    new Vue({
      router,
      store,
      render: h => h(App)
    }).$mount('#app');
  }).catch((error) => {
    console.error('[ERROR] Error on loading runtime configuration json => ', error);
  });

  function DecryptJson(encryptedText: string): string {
    const decrypted = CryptoJS.AES.decrypt(encryptedText, process.env.VUE_APP_ENCRYPTION_KEY, {
      iv: CryptoJS.enc.Utf8.parse(''),
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    });

    return decrypted.toString(CryptoJS.enc.Utf8);
  }
